"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { AnimatePresence, motion } from "motion/react";
// moment-timezone et non moment : .tz() n'existe que si le greffon a patché
// l'instance.
import moment from "moment-timezone";
import { Link, usePathname } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import GroupedAvatar from "@/components/ui/GroupedAvatar/GroupedAvatar";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import { useMessengerNotifications } from "@/contexts/MessengerNotificationsContext";
import { openMessengerWindow } from "@/lib/messengerWindowStore";
import { capitalize } from "@/helpers/capitalize";
import { useMobileView } from "@/components/LargeContents/MessengerComponent/hooks/useMobileView";
import { preloadMessengerWindow } from "./MessengerFloatingWindow";
import { prefetchQuickDiscussions, useQuickDiscussions } from "./useQuickDiscussions";

// Même largeur que le panneau des notifications (UserLayout).
const PANEL_WIDTH = 420;
const PANEL_GAP = 12;
const SCREEN_MARGIN = 8;
const ARROW_INSET = 22;

const shortName = (author) => {
    if (!author) return null;
    if (author.showPseudo && author.pseudo) return capitalize(author.pseudo);
    return author.firstName ? capitalize(author.firstName) : null;
};

// Une ligne de la liste. Mêmes règles d'aperçu que la colonne de la
// messagerie (Sidebar.jsx), dans un format plus serré.
function DiscussionRow({ message, isUnread, session, locale, onPick, t }) {
    const lastMessage = message.lastMessage;
    const lastAuthorIsMe =
        lastMessage?.author?.id != null &&
        lastMessage.author.id == session.user.userId;
    const showAuthorPrefix =
        lastAuthorIsMe || (message.participants?.length || 0) > 2;
    const previewText =
        lastMessage?.excerpt ||
        (lastMessage ? t("messenger_last_message_attachment") : null);
    const preview =
        previewText && showAuthorPrefix
            ? `${lastAuthorIsMe ? t("messenger_last_message_you") : shortName(lastMessage.author)} : ${previewText}`
            : previewText;
    const title =
        message.title === "Conversation privée"
            ? t("messenger_private_discussion")
            : message.title;

    return (
        <button
            type="button"
            onClick={() => onPick(message)}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-100 ${
                isUnread
                    ? "bg-blue-50 hover:bg-blue-100/70 dark:bg-blue-500/10 dark:hover:bg-blue-500/15"
                    : "hover:bg-gray-100 dark:hover:bg-white/5"
            }`}
        >
            <GroupedAvatar
                participants={message.participants}
                currentUserId={session.user.userId}
                size={44}
                className="flex-shrink-0"
            />
            <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                    <span
                        className={`truncate text-[15px] text-gray-900 dark:text-white ${
                            isUnread ? "font-bold" : "font-semibold"
                        }`}
                    >
                        {title}
                    </span>
                    <span className="flex-shrink-0 text-xs text-gray-500 first-letter:uppercase dark:text-grayDark-400">
                        {moment(message.lastActivity || message.creationDate)
                            .tz(session.user.timezone)
                            .locale(locale)
                            .fromNow()}
                    </span>
                </span>
                <span className="flex items-center gap-2">
                    <span
                        className={`min-w-0 flex-1 truncate text-sm ${
                            isUnread
                                ? "text-gray-900 dark:text-white"
                                : "text-gray-600 dark:text-grayDark-400"
                        }`}
                    >
                        {preview || t("messenger_start_conversation")}
                    </span>
                    {isUnread && (
                        <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-blue-500" />
                    )}
                </span>
            </span>
        </button>
    );
}

function ListSkeleton() {
    return (
        <div className="space-y-1 p-2" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((row) => (
                <div key={row} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-full bg-gray-100 dark:bg-white/10" />
                    <div className="flex-1 space-y-2">
                        <div className="h-3 w-2/5 animate-pulse rounded bg-gray-100 dark:bg-white/10" />
                        <div className="h-3 w-4/5 animate-pulse rounded bg-gray-100 dark:bg-white/10" />
                    </div>
                </div>
            ))}
        </div>
    );
}

function DiscussionList({ session, locale, onPick, onClose }) {
    const t = useTranslations();
    const discussions = useQuickDiscussions();
    const { unreadDiscussionIds } = useMessengerNotifications();
    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);

    // loadMore change d'identité à chaque page chargée : l'observateur lit la
    // dernière version sans être recréé.
    const loadMoreRef = useRef(discussions.loadMore);
    useEffect(() => {
        loadMoreRef.current = discussions.loadMore;
    });

    const hasMessages = discussions.messages.length > 0;
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMessages) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMoreRef.current?.();
            },
            { root: scrollRef.current, rootMargin: "120px" },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMessages]);

    return (
        <div className="flex max-h-[inherit] min-h-0 flex-col">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-borderDark">
                <p className="mb-0 text-lg font-bold text-gray-900 dark:text-white">
                    {t("messenger_messages")}
                </p>
                <Link
                    href="/messenger"
                    onClick={onClose}
                    className="flex items-center gap-1 text-sm font-medium text-gray-500 transition-colors duration-100 hover:text-primary dark:text-grayDark-300 dark:hover:text-primary"
                >
                    {t("messenger_quick_open_full")}
                    <SolarIcon name="arrow-right-up-linear" className="h-4 w-4" />
                </Link>
            </div>

            <div
                ref={scrollRef}
                className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-grayDark-600"
            >
                {discussions.isLoading ? (
                    <ListSkeleton />
                ) : !hasMessages ? (
                    <div className="flex flex-col items-center px-8 py-12">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                            <SolarIcon
                                name="chat-round-line-bold-duotone"
                                className="h-8 w-8 text-primary"
                            />
                        </div>
                        <p className="mb-0 text-center font-medium text-gray-900 dark:text-white">
                            {t("messenger_no_message")}
                        </p>
                        <p className="mb-0 mt-1 text-center text-sm text-gray-500 dark:text-grayDark-400">
                            {t("messenger_empty_conversations")}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-0.5 p-2">
                        {discussions.messages.map((message) => (
                            <DiscussionRow
                                key={message.id}
                                message={message}
                                isUnread={unreadDiscussionIds.has(Number(message.id))}
                                session={session}
                                locale={locale}
                                onPick={onPick}
                                t={t}
                            />
                        ))}
                        <div ref={sentinelRef} className="h-px" />
                        {discussions.isLoadingMore && (
                            <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-500 dark:text-grayDark-400">
                                <SolarIcon
                                    name="refresh-bold"
                                    className="h-4 w-4 animate-spin"
                                />
                                {t("messenger_loading")}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * La bulle de messagerie de la barre du haut. Un clic ouvre la liste des
 * dernières discussions sans quitter la page ; choisir une discussion l'ouvre
 * dans la fenêtre flottante (MessengerFloatingWindow).
 *
 * Rien n'est chargé tant qu'on ne s'en approche pas : la liste et le code de
 * la fenêtre partent au survol de la bulle, pour être là au moment du clic.
 */
export default function MessengerQuickAccess({
    className,
    children,
    wrapperClassName = "relative",
    // Appelé à l'ouverture : la barre du haut y referme ses autres panneaux.
    onOpen,
}) {
    const t = useTranslations();
    const { data: session } = useSession();
    const locale = useLocale();
    const pathname = usePathname() || "";
    const isMobileView = useMobileView();
    const { markRead } = useMessengerNotifications();

    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const [theme, setTheme] = useState({ isDark: false, fontFamily: undefined });
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const onMessengerPage = pathname.startsWith("/messenger");

    const close = useCallback(() => setIsOpen(false), []);

    const warmUp = useCallback(() => {
        prefetchQuickDiscussions();
        preloadMessengerWindow();
    }, []);

    const place = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const top = rect.bottom + PANEL_GAP;
        // Le panneau déborde un peu à droite de la bulle : la flèche se pose
        // ainsi sur le bord droit, après le coin arrondi.
        const right = Math.max(
            SCREEN_MARGIN,
            window.innerWidth - rect.right + rect.width / 2 - 8 - ARROW_INSET
        );
        // La flèche vise le milieu de la bulle, même quand le panneau est
        // retenu par le bord de l'écran.
        const arrowRight = Math.max(
            ARROW_INSET,
            window.innerWidth - rect.right + rect.width / 2 - right - 8
        );
        setPosition({ top, right, arrowRight });
        // Le panneau sort par un portail : la classe `dark` vit sur un wrapper
        // du layout, pas sur <html>, il faut la relire au point de départ.
        setTheme({
            isDark: Boolean(trigger.closest(".dark")),
            fontFamily: window.getComputedStyle(trigger).fontFamily,
        });
    }, []);

    useLayoutEffect(() => {
        if (!isOpen || isMobileView) return;
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [isOpen, isMobileView, place]);

    useEffect(() => {
        if (!isOpen || isMobileView) return;
        const onPointerDown = (event) => {
            if (panelRef.current?.contains(event.target)) return;
            if (triggerRef.current?.contains(event.target)) return;
            setIsOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen, isMobileView]);

    const handleTriggerClick = (event) => {
        // Cmd/Ctrl-clic : la messagerie complète dans un nouvel onglet.
        if (event.metaKey || event.ctrlKey) {
            window.open(`/${locale}/messenger`, "_blank", "noopener");
            return;
        }
        if (!isOpen) onOpen?.();
        setIsOpen((open) => !open);
    };

    const handlePick = useCallback(
        (discussion) => {
            markRead(discussion.id);
            openMessengerWindow(discussion, session?.user?.userId);
            setIsOpen(false);
        },
        [markRead, session?.user?.userId],
    );

    return (
        <div ref={triggerRef} className={wrapperClassName}>
            {/* Un bouton, pas un lien : la barre de progression du haut se
                déclenche sur tout clic de lien, même quand on reste sur la
                page. Sur la messagerie elle-même, la bulle redevient un lien. */}
            {onMessengerPage ? (
                <Link
                    href="/messenger"
                    aria-label={t("messenger_messages")}
                    className={className}
                >
                    {children}
                </Link>
            ) : (
                <button
                    type="button"
                    aria-label={t("messenger_messages")}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    onClick={handleTriggerClick}
                    onPointerEnter={warmUp}
                    onFocus={warmUp}
                    className={className}
                >
                    {children}
                </button>
            )}

            {session?.user && isMobileView && (
                <BottomSheetModal
                    isOpen={isOpen}
                    onClose={close}
                    portal
                    zIndexClass="z-[65]"
                    panelClassName="!overflow-hidden flex flex-col"
                >
                    <div className="max-h-[calc(100dvh-60px)] min-h-[50dvh]">
                        {isOpen && (
                            <DiscussionList
                                session={session}
                                locale={locale}
                                onPick={handlePick}
                                onClose={close}
                            />
                        )}
                    </div>
                </BottomSheetModal>
            )}

            {session?.user &&
                !isMobileView &&
                position &&
                createPortal(
                    <div
                        className={theme.isDark ? "dark" : ""}
                        style={{ fontFamily: theme.fontFamily }}
                    >
                        <AnimatePresence>
                            {isOpen && position && (
                                <motion.div
                                    ref={panelRef}
                                    role="dialog"
                                    aria-label={t("messenger_messages")}
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.2, ease: "easeOut" }}
                                    style={{
                                        top: position.top,
                                        right: position.right,
                                        width: PANEL_WIDTH,
                                        maxHeight: `min(560px, calc(100dvh - ${position.top + 16}px))`,
                                    }}
                                    className="fixed z-[100] flex flex-col"
                                >
                                    {/* Flèche pointant sur la bulle */}
                                    <div
                                        style={{ right: position.arrowRight }}
                                        className="absolute -top-[7px] h-4 w-4 rotate-45 rounded-tl-[3px] border-l border-t border-gray-100 bg-white dark:border-borderDark dark:bg-bgDark-840 z-10 [clip-path:polygon(0_0,100%_0,0_100%)]"
                                    />
                                    <div className="relative flex max-h-[inherit] min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl shadow-black/10 dark:border-borderDark dark:bg-bgDark-840 dark:shadow-black/30">
                                        <DiscussionList
                                            session={session}
                                            locale={locale}
                                            onPick={handlePick}
                                            onClose={close}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
